import { useEffect, useState } from "react"

import { usePortalApi } from "./api-context"
import type { PortalView } from "./portal-shell"

/** What waits on the client outside the checklist: open threads where the
 * firm spoke last, and deliveries the client has not confirmed. */
export type PortalAttention = {
  messages: number
  foryou: number
}

const none: PortalAttention = { messages: 0, foryou: 0 }

/**
 * Read on sign-in and again on every section change, so opening Messages
 * or For you and coming back clears what was just read. A failed read shows
 * nothing rather than a wrong count.
 */
export function usePortalAttention(
  signedIn: boolean,
  view: PortalView
): PortalAttention {
  const api = usePortalApi()
  const [attention, setAttention] = useState<PortalAttention>(none)

  useEffect(() => {
    if (!signedIn) {
      setAttention(none)
      return
    }
    let active = true
    void Promise.allSettled([
      api.listThreads(),
      api.listEngagementDeliveries(),
    ]).then(([threads, deliveries]) => {
      if (!active) return
      const waiting =
        threads.status === "fulfilled"
          ? threads.value.threads.filter(
              (thread) => !thread.resolvedAt && thread.lastAuthorSide === "firm"
            ).length
          : 0
      const unconfirmed =
        deliveries.status === "fulfilled"
          ? deliveries.value.deliveries.filter(
              (delivery) => delivery.acknowledgedByMe === false
            ).length
          : 0
      setAttention({ messages: waiting, foryou: unconfirmed })
    })
    return () => {
      active = false
    }
  }, [api, signedIn, view])

  return attention
}
